import { useState, useContext } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, Close, Send } from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";

const WSU_RED = "#A60F2D";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const UnblockMailbox = () => {
  const [state, setState] = useState({ error: null, waiting: false, statusMessage: null, userComment: "" });

  const { setModule } = useContext(ModuleContext);
  const { userModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const networkId = userModule.userData["Network ID"];

  const unblockMailbox = async () => {
    setState((prev) => ({ ...prev, waiting: true, error: null }));

    const payload = {
      networkId,
      comment: state.userComment,
      callerId: authState.accessToken.claims.sub,
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: authState.accessToken.accessToken,
      },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(360000),
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/o365/mailbox/unblock`,
        options
      );

      if (res.status === 504) throw new Error("Gateway Timeout");

      const result = await res.json();

      if (result.error) {
        setState((prev) => ({ ...prev, error: result.error, waiting: false }));
      } else {
        setState((prev) => ({ ...prev, statusMessage: result.message, userComment: "", waiting: false }));
      }
    } catch (error) {
      if (error.message === "Gateway Timeout") {
        const retry = setTimeout(async () => {
          try {
            const retryRes = await fetch(
              `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/user/o365/mailbox/unblock`,
              options
            );
            const retryResult = await retryRes.json();
            if (!retryRes.ok || retryResult.error) {
              setState((prev) => ({ ...prev, error: retryResult.error || { message: "Unknown error" }, waiting: false }));
            } else {
              setState((prev) => ({ ...prev, statusMessage: retryResult.message, userComment: "", waiting: false }));
            }
          } catch (retryError) {
            setState((prev) => ({ ...prev, error: retryError, waiting: false }));
          }
        }, 30000);
        return () => clearTimeout(retry);
      } else {
        setState((prev) => ({ ...prev, error, waiting: false }));
      }
    }
  };

  if (state.waiting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
      </Box>
    );
  }

  if (state.statusMessage) {
    return (
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ backgroundColor: "#2e7d32", color: "white", px: 3, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutline fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Mailbox Unblocked</Typography>
        </Box>
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
            Confirmation
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary">{state.statusMessage}</Typography>
        </Box>
        <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            endIcon={<Close />}
            onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
            sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
          >
            Close
          </Button>
        </Box>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>

      {/* Error Banner */}
      {state.error && (
        <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Action failed</Typography>
          <Typography variant="body2">{state.error.message}</Typography>
        </Box>
      )}

      {/* ── Section: Unblock Mailbox ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Unblock Mailbox
        </Typography>
        <Typography variant="caption" sx={{ display: "block", color: "#777", mt: 0.25 }}>{networkId}</Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* ── Section: Justification ── */}
      <Box sx={{ px: 3, pt: 3, pb: 1 }}>
        <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
          Justification
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 3 }}>
        <TextField
          required
          fullWidth
          size="small"
          multiline
          rows={3}
          label="Reason"
          value={state.userComment}
          onChange={(e) => setState((prev) => ({ ...prev, userComment: e.target.value }))}
          placeholder="Provide a reason for unblocking this mailbox"
          sx={fieldSx}
        />
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          endIcon={<Close />}
          onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
          sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={<Send />}
          onClick={unblockMailbox}
          disabled={!state.userComment.trim()}
          sx={{
            backgroundColor: WSU_RED,
            color: "white",
            px: 4,
            py: 1.25,
            fontWeight: 600,
            letterSpacing: 0.5,
            "&:hover": { backgroundColor: "#8a0c25" },
            "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
          }}
        >
          Unblock Mailbox
        </Button>
      </Box>

    </TableContainer>
  );
};
